import { useCallback, useEffect, useRef, useState } from "react"
import { Head, Link, router, usePoll } from "@inertiajs/react"
import {
  AlertTriangle, ArrowLeft, Check, Clock, Eye, FileText, Globe, Loader2, Pencil, RefreshCw, RotateCcw, Sparkles, Undo2, Upload, Video,
} from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { StatusChip, formatDate } from "./components/bookStatus"
import { BookSectionEditor } from "./components/BookSectionEditor"
import { BrandCheckCard } from "./components/BrandCheckCard"
import { LeaderCard } from "./components/LeaderCard"
import { ChapterOpening } from "@/components/book/ChapterOpening"
import { useFlagHighlights } from "@/hooks/use-flag-highlights"

const ORIGINS = { ai_draft: "AI draft", ai_change: "AI change", coach_edit: "Your edit", own_ai: "From your AI" }
const SOURCE_ICONS = { meeting: Video, article: FileText, upload: Upload, website: Globe }

// The chapter review page: read the latest version (or an older one), then
// approve it, edit it yourself section by section, or ask the AI for a change.
export default function ChapterReview({ currentUser, book, chapter, versions = [], viewing, latestVersion, sources = [], brand }) {
  const { start, stop } = usePoll(6000, { only: ["chapter", "versions", "viewing", "latestVersion", "brand"] }, { autoStart: false })
  const busy = chapter.generating || brand.status === "checking"
  useEffect(() => {
    if (busy) start()
    else stop()
    return () => stop()
  }, [busy])

  const [editing, setEditing] = useState(false)
  const edits = useRef({})
  const [dirty, setDirty] = useState(false)
  const isLatest = viewing && viewing.version === latestVersion
  const approved = chapter.status === "approved"
  const base = `/app/coach/books/${book.id}/chapters/${chapter.id}`
  const locked = book.sentToPrint
  // The Brand check's flagged words, highlighted in the text.
  const page = useRef(null)
  const locate = useFlagHighlights(page, brand.flags, { pickable: !editing, watch: Boolean(viewing) })

  const recordEdit = useCallback((key, part, value, section) => {
    edits.current[key] = { heading: section.heading, body: section.body, ...edits.current[key], [part]: value }
    setDirty(true)
  }, [])

  const cancelEdit = () => {
    edits.current = {}
    setDirty(false)
    setEditing(false)
  }
  const saveEdit = () => {
    router.post(`${base}/edit_sections`, { sections: edits.current }, { onSuccess: cancelEdit })
  }

  let banner = null
  if (chapter.generating) {
    banner = <Banner tone="ai" icon={Loader2} spin><b>The AI is writing.</b> About 3 to 5 minutes. You can leave this page; we email you when it is ready.</Banner>
  } else if (chapter.generationError) {
    banner = <Banner tone="error" icon={AlertTriangle}>{chapter.generationError}</Banner>
  } else if (editing) {
    banner = (
      <Banner tone="edit" icon={Pencil}>
        <span className="flex-1"><b>You are editing the chapter.</b> Click any word and type. Saving makes a new version.</span>
        <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
        <Button size="sm" onClick={saveEdit} disabled={!dirty}>Save as new version</Button>
      </Banner>
    )
  } else if (viewing && !isLatest) {
    banner = (
      <Banner tone="old" icon={Clock}>
        <span className="flex-1">You are looking at <b>version {viewing.version}</b>. This is not the latest version.</span>
        <Button size="sm" variant="outline" asChild><Link href={base}>Show version {latestVersion}</Link></Button>
      </Banner>
    )
  } else if (approved) {
    banner = <Banner tone="ok" icon={Check}><b>Approved.</b> Version {chapter.approvedVersion} goes in the book.</Banner>
  } else if (viewing) {
    banner = <Banner tone="ai" icon={Eye}><b>Version {viewing.version} is ready for review.</b> Read it, then approve it or ask for changes.</Banner>
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${chapter.leaderName} - ${book.title}`} />
      <div className="space-y-5">
        <div className="flex flex-wrap items-start gap-3">
          <Link href={`/app/coach/books/${book.id}`} className="rounded-md p-2 hover:bg-zinc-100" aria-label="Back to the book">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-kf-red">Chapter {chapter.position} of {chapter.chapterCount} · {book.title}</div>
            <h1 className="text-2xl font-bold text-zinc-900">{chapter.leaderName}{viewing?.title ? `: ${viewing.title}` : ""}</h1>
            <p className="text-sm text-zinc-600">
              {[chapter.leaderRole, chapter.leaderCompany, chapter.leaderCity].filter(Boolean).join(" · ")}
              {chapter.wordCount ? ` · about ${chapter.wordCount.toLocaleString()} words` : ""}
            </p>
          </div>
          {!locked && (
            <Button asChild size="sm" variant="outline">
              <Link href={`${base}/edit`}><Pencil className="h-4 w-4" /> Edit leader details</Link>
            </Button>
          )}
          <StatusChip status={chapter.status} />
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-4">
            {banner}
            {viewing ? (
              <article className="kf-book-page rounded-lg border bg-white px-6 py-8 shadow-sm sm:px-10">
                <ChapterOpening {...chapter} title={viewing.title} subtitle={viewing.subtitle} />
                {/* The Brand check reads the sections, not the opening. */}
                <div ref={page}>
                  {viewing.sections.map((section) => (
                    <section key={`${viewing.version}-${section.key}`}>
                      {editing && section.headingEditable ? (
                        <Input className="mb-3 mt-8 font-serif font-bold uppercase" defaultValue={section.heading}
                          aria-label={`${section.label} heading`}
                          onChange={(e) => recordEdit(section.key, "heading", e.target.value, section)} />
                      ) : (
                        <h2>{section.heading}</h2>
                      )}
                      {editing ? (
                        <BookSectionEditor value={section.body} onChange={(html) => recordEdit(section.key, "body", html, section)} />
                      ) : (
                        // The HTML is ChapterTemplate's: sanitized on every write.
                        <div dangerouslySetInnerHTML={{ __html: section.body }} />
                      )}
                    </section>
                  ))}
                </div>
              </article>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-sm text-zinc-600">
                  {chapter.generating ? "The first draft will show here when the AI finishes." : (
                    <>No draft yet. <Link className="underline" href={`${base}/edit`}>Add the details and sources</Link>, then draft the chapter.</>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <aside className="space-y-4">
            {viewing && !locked && (
              <ActionsCard chapter={chapter} base={base} approved={approved} isLatest={isLatest} editing={editing} onEdit={() => setEditing(true)} />
            )}
            {locked && (
              <Card><CardContent className="text-sm text-zinc-600">This book has been sent to print. Nothing in it can change.</CardContent></Card>
            )}
            {viewing && <LeaderCard book={book} chapter={chapter} />}
            {viewing && (
              <BrandCheckCard bookId={book.id} brand={brand} locked={locked} locate={locate}
                hint="The flagged words are highlighted in the chapter. Click them to come back to their flag."
                notCheckedText="Runs when you approve the chapter. It reads the chapter against Kingdom Factor's Statement of Faith and Core Values."
                fixText="Or reopen the chapter and change the words." />
            )}
            {viewing && <CheckCard viewing={viewing} wordCount={chapter.wordCount} shortChapterWords={chapter.shortChapterWords} />}
            {versions.length > 0 && (
              <Card>
                <CardContent className="space-y-2">
                  <b className="text-sm">Versions</b>
                  {versions.map((version) => (
                    <Link key={version.version} href={version.version === latestVersion ? base : `${base}?version=${version.version}`}
                      className={`block rounded-md border px-3 py-2 text-sm hover:border-kf-red ${viewing?.version === version.version ? "border-kf-red bg-kf-red/5" : ""}`}>
                      <div className="flex justify-between">
                        <span className="font-medium">Version {version.version}</span>
                        <span className="text-xs text-zinc-500">{formatDate(version.createdAt)}</span>
                      </div>
                      <div className="text-xs text-zinc-600">
                        {ORIGINS[version.origin]}
                        {chapter.approvedVersion === version.version && " · Approved"}
                      </div>
                      {version.changeNote && <div className="mt-1 text-xs italic text-zinc-500">“{version.changeNote}”</div>}
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}
            <Card>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between">
                  <b className="text-sm">Sources</b>
                  {!locked && <Link href={`${base}/edit`} className="text-xs underline underline-offset-4">Details and sources</Link>}
                </div>
                {sources.map((source) => {
                  const Icon = SOURCE_ICONS[source.kind]
                  return (
                    <div key={source.id} className="flex items-center gap-2 text-sm">
                      <Icon className="h-4 w-4 shrink-0 text-zinc-500" />
                      <span className="min-w-0 flex-1 truncate">{source.label}</span>
                      <span className="text-xs text-zinc-500">{source.words.toLocaleString()} words</span>
                    </div>
                  )
                })}
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </CoachLayout>
  )
}

function Banner({ tone, icon: Icon, spin, children }) {
  const tones = {
    ai: "border-blue-200 bg-blue-50 text-blue-900",
    ok: "border-emerald-200 bg-emerald-50 text-emerald-900",
    old: "border-amber-200 bg-amber-50 text-amber-900",
    edit: "border-zinc-300 bg-zinc-50 text-zinc-900",
    error: "border-red-200 bg-red-50 text-red-900",
  }
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-md border p-3 text-sm ${tones[tone]}`}>
      <Icon className={`h-4 w-4 shrink-0 ${spin ? "animate-spin" : ""}`} />
      {children}
    </div>
  )
}

function ActionsCard({ chapter, base, approved, isLatest, editing, onEdit }) {
  const [note, setNote] = useState("")
  const used = chapter.aiChangesMax - chapter.aiChangesRemaining
  const busy = chapter.generating

  if (approved) {
    return (
      <Card>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-600" /><b>Approved {formatDate(chapter.approvedAt)} · version {chapter.approvedVersion}</b></div>
          <Button variant="outline" className="w-full" onClick={() => router.post(`${base}/reopen`)}>
            <Undo2 className="h-4 w-4" /> Reopen chapter
          </Button>
          <p className="text-xs text-zinc-500">You can reopen it until the book is sent to print.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="space-y-3">
        <Button className="w-full" disabled={editing || !isLatest || busy} onClick={() => router.post(`${base}/approve`)}>
          <Check className="h-4 w-4" /> Approve chapter
        </Button>
        {!isLatest && <p className="text-xs text-zinc-500">Go to the latest version to approve it.</p>}
        <div className="pt-1 text-sm"><b>Want changes?</b> <span className="text-zinc-600">Pick one. You do not have to redo the chapter.</span></div>
        <button type="button" disabled={editing || !isLatest || busy} onClick={onEdit}
          className="flex w-full items-start gap-2 rounded-md border p-3 text-left text-sm hover:border-kf-red disabled:opacity-50">
          <Pencil className="mt-0.5 h-4 w-4 shrink-0" />
          <span><b className="block">Edit it yourself</b><span className="text-zinc-600">Change any word, like in a Word document.</span></span>
        </button>
        <div className="rounded-md border p-3 text-sm">
          <div className="flex items-start gap-2">
            <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">
              <b className="block">Ask the AI for a change</b>
              <span className="text-zinc-600">Say what to change. It changes only that part and keeps the rest.</span>
            </div>
            <span className="flex gap-0.5" title={`${chapter.aiChangesRemaining} of ${chapter.aiChangesMax} AI changes left`}>
              {Array.from({ length: chapter.aiChangesMax }).map((_, i) => (
                <i key={i} className={`h-2.5 w-2.5 rounded-full ${i < used ? "bg-zinc-300" : "bg-kf-red"}`} />
              ))}
            </span>
          </div>
          <Textarea className="mt-2" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000}
            disabled={!chapter.aiRequestAllowed || busy || editing}
            placeholder="For example: Use more of Dana's own words in the Crucible Story." />
          <div className="mt-2 flex items-center gap-2">
            <span className={`flex-1 text-xs ${chapter.aiRequestAllowed ? "text-zinc-500" : "font-semibold text-red-600"}`}>
              {chapter.aiChangesRemaining} of {chapter.aiChangesMax} AI changes left
            </span>
            <Button size="sm" disabled={!note.trim() || !chapter.aiRequestAllowed || busy || editing || !isLatest}
              onClick={() => router.post(`${base}/change`, { note }, { onSuccess: () => setNote("") })}>
              Send to the AI
            </Button>
          </div>
          {!chapter.aiRequestAllowed && <p className="mt-1 text-xs text-zinc-500">You used all {chapter.aiChangesMax} AI changes. You can still edit it yourself.</p>}
        </div>
        <button type="button" disabled={!chapter.aiRequestAllowed || busy || editing}
          onClick={() => {
            if (window.confirm("Write a whole new draft from the sources? This uses one of your AI changes, and your edits to this version are not kept in the new draft.")) {
              router.post(`${base}/draft`)
            }
          }}
          className="flex w-full items-center gap-2 text-xs text-zinc-600 underline-offset-4 hover:underline disabled:opacity-50">
          <RotateCcw className="h-3.5 w-3.5" /> Write a new draft from the sources (uses one AI change)
        </button>
      </CardContent>
    </Card>
  )
}

// The automatic check's warnings for this version (ChapterTemplate#problems),
// plus any section the version is missing.
function CheckCard({ viewing, wordCount, shortChapterWords }) {
  // About 330 words print on a page.
  const short = wordCount && wordCount < shortChapterWords
  const problems = [
    ...viewing.missingSections.map((key) => `${key.replaceAll("_", " ")} is missing`),
    ...viewing.warnings,
  ]
  return (
    <Card>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <b>Template check</b>
          {problems.length === 0 ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" /> All {viewing.sections.length} sections</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {problems.length} to look at</span>
          )}
        </div>
        {problems.length === 0 ? (
          <p className="text-xs text-zinc-600">Every section is there, and the check found no signs of AI writing.</p>
        ) : (
          <ul className="list-disc space-y-1 pl-4 text-xs text-zinc-700">
            {problems.map((problem) => <li key={problem}>{problem}</li>)}
          </ul>
        )}
        {short && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-900">
            <b>A short chapter:</b> {wordCount.toLocaleString()} words, about {Math.ceil(wordCount / 330)} pages. Most chapters are 4,000 to 6,000 words. That is fine if it fits your book. To make it longer, add more sources, then use Redraft.
          </p>
        )}
        <p className="flex items-center gap-1 text-xs text-zinc-500"><Sparkles className="h-3 w-3" /> Checked when the AI writes. Your own edits are yours.</p>
      </CardContent>
    </Card>
  )
}
